import React, { useEffect, useState } from "react";
import UserCreateForm from "./UserCreateForm";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import SmsFrom from "./SmsFrom";
import axiosInstance from "../../../axios/axiosInstance";
import { Zone } from "../../messaging/types";
import { Tooltip } from "../../../components/tooltip/tooltip";
import PermissionGuard from "../../../components/PermissionGuard";
import { getUserPermissions, hasPermissionSync } from "../../../utils/get-user-permissions";
import { path } from "../../../navigation/path";

const getZoneAdminZoneIds = (): number[] => {
  try {
    const s = localStorage.getItem("zone_admin_zone_ids");
    if (!s) return [];
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? arr.map(Number) : [];
  } catch {
    return [];
  }
};

const UserList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role");
  const zoneAdminZoneIds = getZoneAdminZoneIds();
  const isZoneAdmin = zoneAdminZoneIds.length > 0;
  const canEditMember = (member: any) =>
    hasPermissionSync("member.update") || hasPermissionSync("member.manage") || hasPermissionSync("user.update") || hasPermissionSync("user.manage") ||
    (isZoneAdmin && member?.zone?.id != null && zoneAdminZoneIds.includes(Number(member.zone.id)));
  const canDeleteMember = (member: any) =>
    hasPermissionSync("member.delete") || hasPermissionSync("member.manage") || hasPermissionSync("user.delete") || hasPermissionSync("user.manage") ||
    (isZoneAdmin && member?.zone?.id != null && zoneAdminZoneIds.includes(Number(member.zone.id)));
  const canCreateMember = hasPermissionSync("member.create") || hasPermissionSync("member.manage") || hasPermissionSync("user.create") || hasPermissionSync("user.manage") || isZoneAdmin;
  const [loading, setLoading] = useState<boolean>(false);
  const [isCreate, setIsCreate] = useState<Boolean>(false);
  const [users, setUsers] = useState<any>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [id, setId] = useState<any>(null);
  const [page, setPage] = useState<any>(1);
  const [smsModal, setSmsModal] = useState<boolean>(false);
  const [zones, setZones] = useState<Zone[]>([]);

  const handleOpen = () => setIsCreate(true);
  const handleClose = () => {
    handleGetUsers();
    setId(null);
    setIsCreate(false);
  };

  const hadleSmsModalOpen = () => {
    if (selectedIds?.length) {
      setSmsModal(true);
    } else {
      toast.warning("Please select members");
    }
  };
  const hadleSmsModalClose = () => setSmsModal(false);

  const handleGetUsers = async () => {
    setLoading(true);
    try {
      const resp: any = await axiosInstance.get(
        `/api/user?search=${searchQuery}&zone=${zoneFilter}&page=${page}&limit=15`
      );
      if (resp.status === 200) {
        // Backend already filters Super Admin users for non-Super Admin users
        setUsers(resp.data.data);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error("Error getting users");
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id: any) => {
    // Check if the user being deleted is a Super Admin
    // Only Super Admins can delete other Super Admins
    const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true" || Number(role) === 0;
    if (!isSuperAdmin) {
      try {
        // Check if the user has a Super Admin account
        const authResp: any = await axiosInstance.get(`/api/accounts/${id}`);
        if (authResp?.status === 200 && authResp?.data?.data?.role === 0) {
          toast.error("Forbidden: Super Admin users can only be deleted by other Super Admins");
          return;
        }
      } catch (authError: any) {
        // If account doesn't exist, continue (user might not have account)
        // Only block if we confirmed they're Super Admin
        if (authError?.response?.data?.data?.role === 0) {
          toast.error("Forbidden: Super Admin users can only be deleted by other Super Admins");
          return;
        }
      }
    }
    
    try {
      const resp: any = await axiosInstance.delete(
        `/api/user/${id}?invocation=${role}&user=1`
      );
      if (resp.status === 200) {
        if (!Number(role)) {
          toast.success("User deleted successfully");
        } else {
          toast.success("User deletion request sent to Super Admin");
        }
        handleGetUsers();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.body || error?.message;
      if (errorMessage && errorMessage.includes("Super Admin")) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage || "Error deleting user");
      }
    }
  };

  const handleSelectNumbers = (id: string) => {
    setSelectedIds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(users?.data?.map((user: any) => user.id) || []);
    } else {
      setSelectedIds([]);
    }
  };

  useEffect(() => {
    handleGetUsers();
    // eslint-disable-next-line
  }, [searchQuery, zoneFilter, page]);

  useEffect(() => {
    handleGetUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    setLoading(true);
    try {
      // Check if user is Super Admin or has admin permission
      const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true" || Number(role) === 0;
      const hasAdminPermission = hasPermissionSync("admin");
      const canViewAllZones = isSuperAdmin || hasAdminPermission;
      
      if (canViewAllZones) {
        // Super Admin and users with admin permission see all zones
        const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/zone/all`);
        if (res.status === 200 && res.data?.status === "ok") {
          setZones(res.data.data || []);
        }
      } else {
        // Check if user is Zone Admin by calling /api/zone/my-zones
        // If this returns zones, user is a Zone Admin
        try {
          const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/zone/my-zones`);
          if (res.status === 200 && res.data?.status === "ok" && res.data.data && res.data.data.length > 0) {
            // User is a Zone Admin - show only their zones
            setZones(res.data.data || []);
          } else {
            // User is not a Zone Admin - show empty zones list (they won't see any members anyway)
            setZones([]);
          }
        } catch (err) {
          console.error("Failed to fetch zone admin zones:", err);
          // If endpoint fails, user is likely not a Zone Admin
          setZones([]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch zones:", err);
      toast.error("Failed to fetch zones");
    } finally {
      setLoading(false);
    }
  };

  // Table columns: User, Phone, Zone, Address, Channels, Status, Actions
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Member Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Organize and manage your members efficiently
              </p>
            </div>
            {canCreateMember && (
              <button
                onClick={handleOpen}
                className="btn-primary flex items-center gap-2 px-6 py-3 shadow-lg"
              >
                <i className="fa-solid fa-plus"></i>
                <span>Create New Member</span>
              </button>
            )}
          </div>

          {/* Filters Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    placeholder="Search members..."
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                    <i className="fa-solid fa-search"></i>
                  </span>
                </div>
              </div>
              <div className="w-full md:w-auto">
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  className="w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                >
                  <option value="">All Zones</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.zone_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading members...</p>
            </div>
          </div>
        ) : users?.data?.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <i className="fa-solid fa-users text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                No members found
              </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        Get started by creating your first member
                      </p>
              {canCreateMember && (
                <button
                  onClick={handleOpen}
                  className="btn-primary mt-4 flex items-center gap-2 px-6 py-3"
                >
                  <i className="fa-solid fa-plus"></i>
                  <span>Create New Member</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-gray-700">
                <thead className="bg-neutral-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={
                          users?.data?.length > 0 &&
                          selectedIds.length === users?.data?.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="accent-neutral-800 dark:accent-gray-400 w-5 h-5 border-neutral-300 dark:border-gray-600 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Zone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Channels
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-gray-300 tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {users?.data?.map((item: any) => {
                    const statusColor =
                      item.status === "Active"
                        ? "text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-300"
                        : item.status === "Pending"
                        ? "text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-300"
                        : "text-neutral-500 bg-neutral-100 dark:bg-gray-700 dark:text-gray-400";
                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-neutral-50 dark:hover:bg-gray-700 transition"
                      >
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => handleSelectNumbers(item.id)}
                            className="accent-neutral-800 dark:accent-gray-400 w-5 h-5 rounded border-neutral-300 dark:border-gray-600"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {/* <img src={item.avatar} className="w-9 h-9 rounded-full border" alt={item.name} /> */}
                            <span className="font-medium text-neutral-900 dark:text-gray-100">
                              {item.name}
                            </span>
                            {(item.profile?.is_deceased ||
                              item.profile?.date_of_death) && (
                              <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                                Member Deceased
                                {item.profile?.date_of_death
                                  ? ` - ${item.profile?.date_of_death}`
                                  : ""}
                              </span>
                            )}
                            {item.has_pending_request && (
                              <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800">
                                Awaiting Approval
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-neutral-700 dark:text-gray-300">
                          {item.phone}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-neutral-100 dark:bg-gray-700 rounded text-xs text-neutral-700 dark:text-gray-300 capitalize">
                            {item.zone?.zone_name || "None"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {item.city ? (
                            <div className="flex flex-wrap items-center gap-1">
                              {item.city.name && (
                                <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded text-xs font-semibold">
                                  {item.city.name}
                                </span>
                              )}
                              {item.city.taluk?.district?.name && (
                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                                  {item.city.taluk.district.name}
                                </span>
                              )}
                              {item.city.taluk?.district?.state?.name && (
                                <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs">
                                  {item.city.taluk.district.state.name}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-neutral-500 dark:text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-neutral-100 dark:bg-gray-700 rounded text-xs text-neutral-700 dark:text-gray-300 capitalize">
                            {item.address}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span title="SMS user" className="ml-2">
                            <i className="fa-solid fa-sms text-blue-500"></i>
                          </span>
                          {item.is_whatsapp_enabled ? (
                            <span title="WhatsApp user" className="ml-1">
                              <i className="fa-brands fa-whatsapp text-green-500"></i>
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`${statusColor} px-2 py-0.5 rounded text-xs font-semibold`}
                            >
                              {item.status || "N/A"}
                            </span>
                            {item.has_pending_request && (
                              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800">
                                Pending Approval
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 flex gap-2">
                          <Tooltip content="View Details">
                            <button
                              onClick={() =>
                                navigate(`/members/${item.id}`, {
                                  state: { from: location },
                                })
                              }
                              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <i className="fa-solid fa-eye"></i>
                            </button>
                          </Tooltip>
                          {canEditMember(item) && (
                            <Tooltip content="Edit">
                              <button
                                onClick={() => {
                                  handleOpen();
                                  setId(item.id);
                                }}
                                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <i className="fa-solid fa-pen"></i>
                              </button>
                            </Tooltip>
                          )}
                          {canDeleteMember(item) && (
                            <Tooltip content="Delete">
                              <button
                                onClick={() => handleDeleteUser(item.id)}
                                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Pagination */}
        {users?.data?.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-t bg-gray-50 dark:bg-gray-800 mt-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80">
          <div>
            <span className="text-sm text-neutral-500 dark:text-gray-400">
              Showing {users?.data?.length === 0 ? 0 : (page - 1) * 15 + 1}-
              {Math.min(page * 15, users?.total || 0)} of {users?.total || 0}{" "}
              members
            </span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p: number) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 rounded-lg text-neutral-600 dark:text-gray-300 hover:bg-neutral-100 dark:hover:bg-gray-700 disabled:text-neutral-300 dark:disabled:text-gray-600"
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>
            {Array.from(
              { length: users?.totalPages || 1 },
              (_, i) => i + 1
            ).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`px-3 py-1 rounded-lg ${
                  p === page
                    ? "bg-neutral-800 dark:bg-blue-600 text-white"
                    : "text-neutral-600 dark:text-gray-400 hover:bg-neutral-100 dark:hover:bg-gray-700"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() =>
                setPage((p: number) => Math.min(users?.totalPages || 1, p + 1))
              }
              disabled={page >= (users?.totalPages || 1)}
              className="px-3 py-1 rounded-lg text-neutral-600 dark:text-gray-300 hover:bg-neutral-100 dark:hover:bg-gray-700 disabled:text-neutral-300 dark:disabled:text-gray-600"
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>
        </div>
        )}
      </div>
      {/* Add Member Side Panel */}
      {isCreate && (
        <UserCreateForm onClose={handleClose} id={id} />
      )}
    </div>
  );
};

export default UserList;
